import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

type AccessState =
  | 'active'
  | 'trial_active'
  | 'trial_expired'
  | 'grace_soft'
  | 'grace_hard'
  | 'blocked'
  | 'subscription_suspended'
  | 'account_suspended';

interface AccountAlert {
  code: string;
  severity: 'info' | 'warning' | 'critical' | 'blocking';
  title: string;
  message: string;
  cta_url: string | null;
  level: number;
  dismissable: boolean;
  countdown_date: string | null;
}

interface AccountStatusData {
  access_state: AccessState;
  subscription_status: string | null;
  days_to_expiry: number;
  grace_days_left: number;
  /** Authoritative days elapsed since expiry (server-computed). Clients should
   *  prefer this over reconstructing `HARD_GRACE_DAYS - grace_days_left`. */
  days_since_expiry?: number;
  trial_days_left: number;
  blocking: boolean;
  write_restricted: boolean;
  sync_blocked: boolean;
  alerts: AccountAlert[];
  /** Plan id to pre-select on checkout (current plan, or recommended upsell). */
  recommended_plan_id?: string | null;
  /** Last successful payment method (e.g. mobile_money, om, momo, cinetpay). */
  last_payment_method?: string | null;
}

export function useAccountStatus() {
  return useQuery({
    queryKey: ['account', 'status'],
    queryFn: async () => {
      const response = await api.get<AccountStatusData>('/account/status');
      return response.data;
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
}

export type { AccountStatusData, AccountAlert, AccessState };
